// The Campaigns assistant: types (mirroring crates/api-types/src/campaigns.rs and
// crates/core/src/agent.rs) and the API calls (docs/contract.md, section 15).

import type { ApiTransport } from "./client.ts";
import type {
  ImportCleanupPayload,
  ImportPayload,
  ImportScrubPayload,
  ImportTiersPayload,
} from "./imports.ts";
import type { LeadGroupPayload, ResearchPayload, ResearchSources } from "./research.ts";
import type {
  AssistantSession,
  CallList,
  ChatStreamEvent,
  ConversationSummary,
  CrmRef,
  E164,
  ListTally,
  MarkWonStatus,
  StatusCounts,
  Uuid,
} from "./types.ts";

// Campaigns ---------------------------------------------------------------------------------

/** `owner` Only you, `team` Your team, `members` Everyone in the top-level campaign. */
export type CampaignVisibility = "members" | "team" | "owner";
export type CampaignStatus = "active" | "archived";
/** `pending`: a private sub-campaign's tag, made in Odoo when it is shared. */
export type TagState = "none" | "pending" | "ok" | "failed";
export type PauseScope = "contact" | "list" | "campaign" | "user" | "tenant";

export interface PausedBy {
  pause_id: Uuid;
  scope: PauseScope;
  until: string | null;
  /** Paused for every rep, not just this one. */
  everyone: boolean;
}

export interface AiState {
  active: boolean;
  paused_by: PausedBy | null;
}

export interface CampaignTag {
  state: TagState;
  crm_id: string | null;
  /** The tag's full path; for a pending tag, the path it will get. */
  name: string | null;
  error: string | null;
}

/** The owner avatar's person (initials on their tint, the name in the tooltip). */
export interface CampaignOwner {
  user_id: Uuid;
  name: string;
  /** Their photo's version (ADR 0029); absent: initials on their tint. */
  photo_version?: string;
}

export interface CampaignNode {
  id: Uuid;
  parent_id: Uuid | null;
  root_id: Uuid;
  depth: number;
  name: string;
  /** The brief's one line (at most 200 characters); empty until written. In the tree too. */
  summary?: string;
  /** The brief's document (Markdown). Empty in the tree. */
  brief_md: string;
  /** The brief's latest version; send it back as `CampaignPatch.brief_version`. */
  brief_version?: number;
  owner_user_id: Uuid | null;
  owner_name: string | null;
  /** The owner, else the admin who made it. Older servers leave it out. */
  owner?: CampaignOwner | null;
  mine: boolean;
  visibility: CampaignVisibility;
  allow_user_subs: boolean;
  tag: CampaignTag;
  /** The tag it gets by default: "Campaign / Hyperprints / Nebraska". */
  tag_default: string;
  status: CampaignStatus;
  list_count: number;
  pending_suggestions: number;
  ai: AiState;
  children: CampaignNode[];
  created_at: string;
  updated_at: string;
}

export interface CampaignRef {
  id: Uuid;
  name: string;
}

export type MemberRole = "lead" | "member";

export interface CampaignMember {
  user_id: Uuid;
  name: string;
  email: string;
  role: MemberRole;
}

export interface CampaignStats {
  from: string;
  to: string;
  calls: number;
  connected: number;
  voicemails: number;
  opportunities_created: number;
  follow_ups_booked: number;
}

export interface CampaignList {
  id: Uuid;
  name: string;
  campaign_id: Uuid;
  owner_name: string | null;
  /** Everyone on the list. */
  total: number;
  /** Done as the Lists screen counts it: done, closed and scheduled. */
  done: number;
  /** Per-status counts, the same as a list's own `counts`. */
  counts: StatusCounts;
  mine: boolean;
  /** The list's owner, to split Mine and Team (ADR 0019). */
  owner_user_id?: Uuid | null;
  /** People by progress and by disposition. Older servers leave it out. */
  tally?: ListTally;
}

export interface CampaignDetail {
  campaign: CampaignNode;
  path: CampaignRef[];
  members: CampaignMember[];
  lists: CampaignList[];
  stats_30d: CampaignStats;
  /** Edit and Refine this brief: admins, the owner, a lead manager, a manager of the owner. */
  can_edit: boolean;
  can_add_sub: boolean;
  /** The caller may change its Odoo tag. */
  can_tag: boolean;
  /** Sharing on a sub-campaign: its owner, a manager of the owner, admins. */
  can_share?: boolean;
  /** The People tab's Add and Remove: admins and managers who lead the campaign. */
  can_manage_members?: boolean;
  /** People in the top-level campaign (sub-campaigns share their root's members). */
  member_count: number;
}

/**
 * The category a new Odoo tag goes under: an existing tag, or a new category (under an existing
 * tag, or top level). `name` and `parent_name` are full paths, for display only.
 */
export type TagCategoryChoice =
  | { kind: "existing"; crm_id: string; name?: string | null }
  | { kind: "new"; name: string; parent_id?: string | null; parent_name?: string | null };

/**
 * How a campaign gets its Odoo tag. `custom` makes the tag `name` in `category` (top level
 * without one); a `name` with slashes and no category is the older path form. Ids never show.
 */
export type TagChoice =
  | { mode: "create" }
  | { mode: "link"; crm_id: string; name?: string | null }
  | { mode: "custom"; name: string; category?: TagCategoryChoice | null }
  | { mode: "none" };

export interface TagOption {
  crm_id: string;
  /** Full path, as Odoo shows it. */
  name: string;
}

export interface TagSearch {
  items: TagOption[];
  /** "Campaign": the namespace parent of campaign tags. */
  campaign_namespace: string;
  /** The caller's Odoo user may create tags; `false` only when Odoo said no. */
  can_create: boolean;
}

export interface NewCampaign {
  /** Absent: a top-level campaign (admins and managers). */
  parent_id?: Uuid | null;
  name: string;
  summary?: string | null;
  brief_md?: string | null;
  allow_user_subs?: boolean | null;
  tag?: TagChoice | null;
  /** Top-level only: managers may add only people they manage. */
  members?: Uuid[];
  /** Default `owner` for a rep's sub-campaign, `members` for an admin's or manager's. */
  visibility?: CampaignVisibility | null;
}

export interface CampaignPatch {
  name?: string;
  summary?: string;
  brief_md?: string;
  /** The version the edit started from: `409 brief_conflict` when the brief moved on. */
  brief_version?: number;
  visibility?: CampaignVisibility;
  status?: CampaignStatus;
  allow_user_subs?: boolean;
}

export type BriefSource = "original" | "manual" | "interview" | "refine" | "undo" | "restore";

export interface BriefVersion {
  version: number;
  summary: string;
  brief_md: string;
  source: BriefSource;
  edited_by: Uuid | null;
  edited_by_name: string | null;
  edited_at: string;
}

export interface BriefVersions {
  /** Newest first: the last 50 and the original. */
  items: BriefVersion[];
  current: number;
}

/** The starter text of a new brief: the company's template, else the product default. */
export interface BriefTemplate {
  body_md: string;
  custom: boolean;
  default_md: string;
  updated_at: string | null;
  updated_by_name: string | null;
}

export interface CampaignPerson {
  user_id: Uuid;
  name: string;
  email: string;
  role: MemberRole;
  can_remove: boolean;
  /** The only lead: stays until another lead is made. */
  last_lead: boolean;
  /** The caller manages them ("Your team"). Older servers leave it out. */
  your_team?: boolean;
  /** Who added them, when known. */
  added_by_name?: string | null;
  added_at?: string | null;
}

export interface PersonRef {
  user_id: Uuid;
  name: string;
  email: string;
  /** The caller manages them. */
  your_team?: boolean;
}

/** The People tab: the members of the campaign's top-level campaign. */
export interface CampaignPeople {
  campaign_id: Uuid;
  /** Leads first, then by name. */
  members: CampaignPerson[];
  can_add: boolean;
  can_make_lead: boolean;
  /** Who Add people offers: people the caller manages (admins: everyone) not in it yet. */
  addable: PersonRef[];
}

export interface CampaignTree {
  items: CampaignNode[];
  /** New campaign in the tree header: admins and managers. Older servers leave it out. */
  can_create?: boolean;
}

// Instructions ------------------------------------------------------------------------------

export type InstructionScope = "tenant" | "user" | "user_campaign";

export interface Instruction {
  scope: InstructionScope;
  campaign_id: Uuid | null;
  body_md: string;
  /** 0 before the first save. */
  version: number;
  max_chars: number;
  updated_at: string | null;
  updated_by_name: string | null;
}

export interface BriefEntry {
  campaign_id: Uuid;
  name: string;
  brief_md: string;
}

export interface InstructionBundle {
  fundamentals: string;
  briefs: BriefEntry[];
  global: Instruction;
  campaign: Instruction | null;
}

export interface InstructionVersion {
  version: number;
  body_md: string;
  updated_at: string;
  updated_by_name: string | null;
}

export interface PromptPreview {
  prompt: string;
  prompt_version: string;
  sections: { name: string; chars: number; trimmed: boolean }[];
  total_chars: number;
}

// Suggestions -------------------------------------------------------------------------------

export type SuggestionKind =
  | "call_list"
  | "list_changes"
  | "follow_ups"
  | "import"
  | "import_cleanup"
  | "import_scrub"
  | "import_tiers"
  | "lead_group"
  | "research"
  | "sub_campaign"
  | "list_move"
  | "instructions"
  | "idea"
  | "mark_done"
  | "brief_change"
  | "tracker_status"
  | "mark_won"
  | "contact_details";

export type SuggestionStatus =
  | "pending"
  | "applying"
  | "applied"
  | "failed"
  | "dismissed"
  | "expired"
  | "superseded";

export interface ContactKey {
  model: string;
  id: string;
}

export interface ProposedContact {
  contact: ContactKey;
  name?: string | null;
  company?: string | null;
  reason?: string;
  /** The segment it is in: one of the list's `segments` by name. */
  segment?: string | null;
  /** Other numbers to try, in full, filled by the server from the CRM. */
  alternates?: AltPhone[];
}

/** Another number for a person on a proposed list ("Mobile", "Company: Acme Prints"). */
export interface AltPhone {
  label: string;
  phone: E164;
}

/** A named group of a proposed list, in calling order ("Back today"). */
export interface ListSegment {
  name: string;
  description?: string | null;
}

export interface LeftOut {
  paused: number;
  no_phone: number;
  not_found: number;
  /** Every phone of the contact is on a do-not-call list. Missing on older cards. */
  dnc?: number;
}

export interface CallListPayload {
  name: string;
  description?: string | null;
  campaign_id?: Uuid | null;
  items: ProposedContact[];
  order_hint?: string | null;
  left_out?: LeftOut;
  /** Named groups in calling order; absent for a list in one group. */
  segments?: ListSegment[];
  /** Odoo activities the list came from; the card offers to mark them done too. */
  activities?: DoneActivity[];
  /** The rep ticked "Also mark these activities done" (off by default). */
  mark_done?: boolean;
  /**
   * Who the list is for when a manager makes it for a rep (ADR 0019); absent: the person making
   * it. A list for someone else never offers to mark the activities done.
   */
  for_user_id?: Uuid | null;
  /** The rep's name when the list is for someone else ("Dana Ruiz"). */
  for_name?: string | null;
  /** People without a phone: the card's No phone group, off unless `include_no_phone`. */
  no_phone_people?: ProposedContact[];
  /** The rep turned the No phone group on: they join the list marked No phone. */
  include_no_phone?: boolean;
  /** People with AI paused: shown marked, never added. */
  paused_people?: ProposedContact[];
  /** What a list from the CRM's quotes and orders was found from (ADR 0040). */
  found?: FoundCounts | null;
}

/** "1,240 orders · 312 customers" on a list made from CRM records. */
export interface FoundCounts {
  /** "orders", "quotes" or "quotes and orders". */
  noun: string;
  records: number;
  customers: number;
  /** The read stopped at its safety cap or time limit. */
  capped?: boolean;
}

/** One Odoo activity (mail.activity id) a card would mark done. */
export interface DoneActivity {
  id: string;
  /** The record it is on. */
  name?: string | null;
  /** Its title, like "Outgoing Call (Answered)". */
  summary?: string | null;
  reason?: string;
}

/**
 * `mark_won`: the order suggestion (docs/specs/dashboards.md 2.11). A confirmed sales order of
 * the customer of an open opportunity; Mark won marks it won in Odoo with the rep's own key.
 */
export interface MarkWonPayload {
  /** The crm.lead id. */
  crm_id: string;
  opportunity_name: string;
  stage?: string | null;
  expected_revenue?: number | null;
  opportunity_url?: string | null;
  /** The sale.order id. */
  order_id: string;
  order_name: string;
  customer: string;
  amount: number;
  currency?: string | null;
  ordered_at: string;
  order_url?: string | null;
  reason: string;
}

/** A US postal address in parts (`vd_core::site_contact::PostalAddress`). */
export interface PostalAddress {
  street?: string | null;
  city: string;
  /** Two-letter state code. */
  state: string;
  zip?: string | null;
}

/** One contact's email and address read on its own website, with the quotes. */
export interface ContactDetailsItem {
  contact: ContactKey;
  name: string;
  website: string;
  email?: string | null;
  address?: PostalAddress | null;
  evidence: { field: string; url: string; quote: string }[];
}

/** One contact's Odoo write after a `contact_details` card was accepted. */
export interface ContactDetailsSaved {
  state: "delivered" | "failed";
  /** Why it failed: `reconnect`, `forbidden`, `not_found`, `not_connected`, `unreachable`, `odoo`. */
  code?: string | null;
  /** The fields written. */
  fields?: string[];
  /** The address part Odoo already had that differs from the site's: no address part was written. */
  skipped?: "city" | "state" | "zip" | null;
}

/**
 * `contact_details` (R-9): emails and addresses read on CRM contacts' own websites. Accept
 * writes them to Odoo with the rep's own key, into empty fields only.
 */
export interface ContactDetailsPayload {
  items: ContactDetailsItem[];
}

/** `mark_done`: close Odoo activities with the rep's own key once the rep confirms. */
export interface MarkDonePayload {
  activities: DoneActivity[];
  feedback?: string | null;
  /** Ids Odoo did not show (done already, deleted or not visible). */
  not_found?: number;
}

/**
 * `brief_change`: one change to a campaign's brief (Refine this brief, ADR 0027). `find` is text
 * as it stood in the brief (empty to add a new part under `heading`), `replace` the new text, in
 * the brief's Markdown. `summary`: the change is to the one-line summary.
 */
export interface BriefChangePayload {
  find: string;
  replace: string;
  heading?: string | null;
  summary?: boolean;
  reason?: string;
  base_version?: number;
  /** The heading the change falls under, or the new heading it adds. */
  section?: string | null;
  /** The change adds a new heading. */
  new_part?: boolean;
}

/** How an applied brief change is taken back. */
export interface BriefUndo {
  summary: boolean;
  find: string;
  replace: string;
}

/** What marking activities done did, per activity id. */
export interface MarkDoneResult {
  done?: string[];
  already_done?: string[];
  failed?: { id?: string; name?: string | null; error: string; code?: string }[];
  total?: number;
  say?: string;
}

export interface ListChangesPayload {
  list_id: Uuid;
  list_name?: string | null;
  add: ProposedContact[];
  remove: { item_id: Uuid; name?: string | null; reason?: string }[];
  move_to_top: Uuid[];
  left_out?: LeftOut;
}

export interface FollowUpItem {
  contact: ContactKey;
  name?: string | null;
  /** The opportunity (crm.lead) the follow-up is booked on, checked by the server. */
  deal?: ContactKey | null;
  /** The opportunity's name, filled by the server. */
  opportunity_name?: string | null;
  /** YYYY-MM-DD */
  due_date: string;
  kind?: string | null;
  activity_type_id?: string | null;
  summary: string;
  note?: string | null;
  reason?: string;
}

export interface FollowUpsPayload {
  items: FollowUpItem[];
  left_out?: LeftOut;
}

/** A list a card moves, and where it is now ("Hyperprints", or null for no campaign). */
export interface ListRef {
  list_id: Uuid;
  name: string;
  from?: string | null;
}

/** What a sub-campaign card does right after making the sub-campaign. */
export type FollowOn =
  | { kind: "research"; research: ResearchPayload }
  | { kind: "import"; attachment_id: Uuid; file_name: string; rows: number }
  | { kind: "call_list"; list: CallListPayload }
  | { kind: "move_lists"; lists: ListRef[] };

export interface SubCampaignPayload {
  parent_id: Uuid;
  /** "Hyperprints" or "Hyperprints / East Coast". */
  parent_path: string;
  name: string;
  brief_md: string;
  tag: TagChoice;
  /** The tag's path for the card ('Campaign / Hyperprints / Nebraska'); empty for no tag. */
  tag_path: string;
  /** Shared with the campaign's members from the start (default: only the rep). */
  shared: boolean;
  next?: FollowOn | null;
}

export interface ListMovePayload {
  /** Where the lists go; null takes them out of any campaign. */
  campaign_id: Uuid | null;
  campaign_path: string;
  lists: ListRef[];
}

export interface SuggestionResult {
  list_id?: Uuid;
  name?: string;
  added?: number;
  removed?: number;
  moved?: number;
  created?: { index?: number; contact: string; activity: string }[];
  failed?: {
    index?: number;
    contact?: string;
    id?: string;
    name?: string | null;
    error: string;
    code?: string;
  }[];
  /** A tracker card applied with "Let me pick": the contacts left out (`res.partner:12`). */
  skipped_contacts?: string[];
  /** Mark done: activity ids closed, closed before this ran, and how many in all. */
  done?: string[];
  already_done?: string[];
  total?: number;
  /** A list card whose activities the rep also asked to mark done. */
  mark_done?: MarkDoneResult;
  /** Mark won: the request queued on accept (its outcome arrives as `opportunity.updated`). */
  mark_won?: MarkWonStatus;
  /** Contact details: contacts queued for Odoo. */
  queued?: number;
  /** Contact details: each queued contact's outcome once its Odoo write settles, by contact id. */
  saved?: Record<string, ContactDetailsSaved>;
  /** Follow-ups (by index) whose day had passed when a retry booked them, moved to today. */
  moved_to_today?: number[];
  noted?: boolean;
  error?: string;
  /** Brief change: the version written, how to undo it, the text applied, and the undo. */
  version?: number;
  undo?: BriefUndo;
  applied?: string;
  undone?: boolean;
  undo_version?: number;
  /** Applied from chat at the person's typed request (`apply_cards`, spec 2.19). */
  at_request?: { message_id: Uuid; at: string; session_id?: Uuid };
  /** Undo of a change applied at request: when, and what it did. */
  undone_at?: string;
  undo_detail?: {
    back?: number;
    closed?: number;
    removed?: number;
    restored?: number;
    kept?: number;
    skipped?: { name?: string; why: string }[];
  };
  /** Imports: the assistant's next line, and counts. */
  imported?: number;
  not_imported?: number;
  say?: string;
  batch_id?: Uuid;
  rows?: number;
  create?: number;
  link?: number;
  review?: number;
  skip?: number;
  linked?: number;
  skipped?: boolean;
  /** Research: the run started on accept. */
  run_id?: Uuid;
  /** Sub-campaign: the one made, its path, and what the next step did. */
  campaign_id?: Uuid | null;
  path?: string;
  next?: {
    kind: FollowOn["kind"];
    suggestion_id?: Uuid;
    batch_id?: Uuid;
    list_id?: Uuid;
    name?: string;
    added?: number;
    moved?: number;
    error?: string;
  };
  next_failed?: boolean;
}

interface SuggestionBase {
  id: Uuid;
  campaign_id: Uuid | null;
  campaign_name: string | null;
  session_id: Uuid | null;
  title: string;
  body_md: string;
  status: SuggestionStatus;
  edited: boolean;
  run_kind: string | null;
  result: SuggestionResult | null;
  created_at: string;
  expires_at: string | null;
  decided_at: string | null;
}

/** A tracker status change proposed in chat (`set_tracker_status`, spec 2.17). */
export interface TrackerStatusPayload {
  tracker_id: Uuid;
  tracker_name: string;
  item_noun: string;
  contact: CrmRef;
  contact_name: string;
  /** Null when applying it starts a new item. */
  from: string | null;
  from_label: string | null;
  to: string;
  to_label: string;
  note: string | null;
  /** More contacts moving to the same status on this card; absent on a card for one. */
  more?: TrackerStatusMore[];
}

/** One more contact on a `tracker_status` card. */
export interface TrackerStatusMore {
  contact: CrmRef;
  contact_name: string;
  from: string | null;
  from_label: string | null;
  note: string | null;
}

export type Suggestion =
  | (SuggestionBase & { kind: "call_list"; payload: CallListPayload })
  | (SuggestionBase & { kind: "list_changes"; payload: ListChangesPayload })
  | (SuggestionBase & { kind: "follow_ups"; payload: FollowUpsPayload })
  | (SuggestionBase & { kind: "import_cleanup"; payload: ImportCleanupPayload })
  | (SuggestionBase & { kind: "import_scrub"; payload: ImportScrubPayload })
  | (SuggestionBase & { kind: "import_tiers"; payload: ImportTiersPayload })
  | (SuggestionBase & { kind: "import"; payload: ImportPayload })
  | (SuggestionBase & { kind: "research"; payload: ResearchPayload })
  | (SuggestionBase & { kind: "lead_group"; payload: LeadGroupPayload })
  | (SuggestionBase & { kind: "sub_campaign"; payload: SubCampaignPayload })
  | (SuggestionBase & { kind: "list_move"; payload: ListMovePayload })
  | (SuggestionBase & { kind: "mark_done"; payload: MarkDonePayload })
  | (SuggestionBase & { kind: "brief_change"; payload: BriefChangePayload })
  | (SuggestionBase & { kind: "tracker_status"; payload: TrackerStatusPayload })
  | (SuggestionBase & { kind: "mark_won"; payload: MarkWonPayload })
  | (SuggestionBase & { kind: "contact_details"; payload: ContactDetailsPayload })
  | (SuggestionBase & {
      kind: Exclude<
        SuggestionKind,
        | "call_list"
        | "list_changes"
        | "follow_ups"
        | "import_cleanup"
        | "import_scrub"
        | "import_tiers"
        | "import"
        | "research"
        | "lead_group"
        | "sub_campaign"
        | "list_move"
        | "mark_done"
        | "brief_change"
        | "tracker_status"
        | "mark_won"
        | "contact_details"
      >;
      payload: Record<string, unknown>;
    });

export interface SuggestionList {
  items: Suggestion[];
  total: number;
}

export interface PausedSubjects {
  contacts: ContactKey[];
  lists: Uuid[];
  campaigns: Uuid[];
}

export interface DismissSuggestion {
  reason?: string | null;
  pause?: "contacts" | "campaign" | null;
  until?: string | null;
}

// Pauses ------------------------------------------------------------------------------------

export interface AiPause {
  id: Uuid;
  scope: PauseScope;
  contact: CrmRef | null;
  list_id: Uuid | null;
  campaign_id: Uuid | null;
  user_id: Uuid | null;
  everyone: boolean;
  until: string | null;
  reason: string | null;
  created_by: Uuid | null;
  created_at: string;
  label: string | null;
}

export interface NewPause {
  scope: PauseScope;
  contact?: CrmRef | null;
  list_id?: Uuid | null;
  campaign_id?: Uuid | null;
  user_id?: Uuid | null;
  until?: string | null;
  reason?: string | null;
  everyone?: boolean;
}

/** One subject for the AI switch. */
export type AiSubject =
  | { contact: CrmRef }
  | { list_id: Uuid }
  | { campaign_id: Uuid }
  | Record<string, never>;

// Settings and usage ------------------------------------------------------------------------

export interface AgentModels {
  chat: string;
  voice: string;
  digest: string;
  plan: string;
  research: string;
  mapping: string;
}

export interface AgentSettings {
  enabled: boolean;
  background: boolean;
  review_time: string;
  review_days: number[];
  daily_usd_per_user: number;
  monthly_usd_tenant: number;
  models: AgentModels;
  imports_max_rows: number;
  imports_rollback_days: number;
  research_max_candidates_per_run: number;
  research_runs_per_user_per_day: number;
  research_max_usd_per_run?: number;
  research_sources?: ResearchSources;
  tag_namespaces: { campaign: string; imports: string; source: string };
  /** The most one Assistant message may spend on lookups before it answers (default $1). */
  turn_max_usd?: number;
}

export interface Budget {
  remaining_usd: number;
  exhausted: boolean;
  band: "plenty" | "low" | "none";
  binding: "rep_daily" | "tenant_monthly";
}

export interface BudgetView {
  budget: Budget;
  spent_today_usd: number;
  enabled: boolean;
}

// Conversation list ---------------------------------------------------------------------------

export type TitleSource = "model" | "first" | "rep";

/** Who writes a Campaigns conversation: the app, or the rep's own AI through the connector. */
export type ConversationChannel = "app" | "connector";

/** Where a connector conversation's connection stands. */
export type ConnectorState = "connected" | "disconnected" | "revoked_by_admin";

/** The connection behind a connector conversation (Claude or ChatGPT through Kite). */
export interface ConversationConnector {
  /** As the client registered it: "Claude" or "ChatGPT". */
  client_name: string;
  state: ConnectorState;
  connected_at: string | null;
  last_used_at: string | null;
  /** When it ended (not `connected`). */
  ended_at: string | null;
}

/** `connector.cards`: the rep's Claude or ChatGPT added cards (once per call). */
export interface ConnectorCards {
  session_id: Uuid;
  client_name: string;
  suggestion_ids: Uuid[];
}

/** One conversation in the Assistant's list (`GET /v1/assistant/conversations`). */
export interface ConversationItem {
  id: Uuid;
  /** Older servers leave it out (then `app`). */
  channel?: ConversationChannel;
  /** Connector conversations: the client and its connection. */
  connector?: ConversationConnector | null;
  title: string;
  title_source: TitleSource | null;
  campaign_id: Uuid | null;
  /** "Hyperprints / Nebraska" while the rep can see the campaign. */
  campaign_name: string | null;
  created_at: string;
  last_activity_at: string;
  pinned: boolean;
  /** Suggestion cards waiting for the rep. */
  pending_cards: number;
  /** With a search: the start of the first line that matched. */
  snippet: string | null;
  /** Archived by the rep: listed under Archived only. Older servers leave it out. */
  archived?: boolean;
}

/** How many conversations each view holds, under the same campaign filter and search. */
export interface ConversationCounts {
  active: number;
  archived: number;
}

export interface ConversationPage {
  /** Pinned conversations (first page of the active view without a search only). */
  pinned: ConversationItem[];
  items: ConversationItem[];
  next_cursor: string | null;
  /** First page only. Older servers leave it out. */
  counts?: ConversationCounts | null;
}

export interface ConversationSearch {
  campaign_id?: Uuid | null;
  q?: string | null;
  cursor?: string | null;
  limit?: number;
  /** `true`: the archived conversations. Otherwise the active ones. */
  archived?: boolean;
}

export interface ConversationDeleted {
  id: Uuid;
  expired_cards: number;
}

// Screen context ----------------------------------------------------------------------------

export interface ScreenContext {
  route: string;
  campaign_id?: Uuid | null;
  list_id?: Uuid | null;
  contact?: CrmRef | null;
  call_id?: Uuid | null;
  selection?: { kind: "contacts" | "list_items"; count: number; refs: CrmRef[] } | null;
  /** The email design open in the designer and the node selected on its canvas; the server
   * scopes the conversation to it (stories EM20, EM25). */
  design?: DesignFocus | null;
}

/** `DesignFocus`: the email design the Assistant sits beside, and the node selected on its
 * canvas (`sec5`, `txt2`). */
export interface DesignFocus {
  id: Uuid;
  selected_node_id?: string | null;
}

export type CampaignsChatEvent =
  | ChatStreamEvent
  | { type: "suggestion"; suggestion: Suggestion }
  /** An artifact was built or changed: the canvas shows it (ADR 0017). */
  | { type: "artifact"; artifact: import("./artifacts.ts").Artifact }
  | { type: "budget"; remaining_usd: number; exhausted: boolean };

// API ---------------------------------------------------------------------------------------

function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== null && v !== undefined && v !== "") u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : "";
}

/** `423 ai_paused` from Accept. */
export class PausedError extends Error {
  readonly paused: PausedSubjects;
  constructor(paused: PausedSubjects) {
    super("Some of this was paused since it was suggested.");
    this.name = "PausedError";
    this.paused = paused;
  }
}

/** Campaigns, suggestions, pauses and the assistant's instruction files. */
export class CampaignsApi {
  constructor(private readonly client: ApiTransport) {}

  // Campaigns
  tree(signal?: AbortSignal) {
    return this.client.get<CampaignTree>("/v1/campaigns", signal);
  }
  detail(id: Uuid, signal?: AbortSignal) {
    return this.client.get<CampaignDetail>(`/v1/campaigns/${id}`, signal);
  }
  create(body: NewCampaign) {
    return this.client.post<CampaignDetail>("/v1/campaigns", body);
  }
  update(id: Uuid, body: CampaignPatch) {
    return this.client.patch<CampaignDetail>(`/v1/campaigns/${id}`, body);
  }
  /** Makes the tag again, or changes it to `tag`. */
  retryTag(id: Uuid, tag?: TagChoice) {
    return this.client.post<CampaignDetail>(`/v1/campaigns/${id}/tag`, tag ? { tag } : {});
  }
  /** The brief's saved versions, newest first. */
  briefVersions(id: Uuid, signal?: AbortSignal) {
    return this.client.get<BriefVersions>(`/v1/campaigns/${id}/brief/versions`, signal);
  }
  /** Makes an earlier version the brief again (saved as a new version). */
  restoreBrief(id: Uuid, version: number) {
    return this.client.post<CampaignDetail>(`/v1/campaigns/${id}/brief/restore`, { version });
  }
  /** What a new brief starts from. */
  briefTemplate(signal?: AbortSignal) {
    return this.client.get<BriefTemplate>("/v1/brief-template", signal);
  }
  /** The People tab of the campaign's top-level campaign. */
  people(id: Uuid, signal?: AbortSignal) {
    return this.client.get<CampaignPeople>(`/v1/campaigns/${id}/members`, signal);
  }
  /** Adds someone (or, for admins, changes their role). */
  addPerson(id: Uuid, userId: Uuid, role?: MemberRole) {
    return this.client.put<CampaignPeople>(`/v1/campaigns/${id}/members`, {
      user_id: userId,
      ...(role ? { role } : {}),
    });
  }
  removePerson(id: Uuid, userId: Uuid) {
    return this.client.del<CampaignPeople>(`/v1/campaigns/${id}/members/${userId}`);
  }
  /** Odoo tags whose path contains `q`, best first (at most 30). */
  tags(q: string, signal?: AbortSignal) {
    return this.client.get<TagSearch>(`/v1/campaigns/tags${qs({ q })}`, signal);
  }
  /** Puts a list into a campaign, or takes it out (`null`). */
  moveList(listId: Uuid, campaignId: Uuid | null) {
    return this.client.put<CallList>(`/v1/lists/${listId}/campaign`, { campaign_id: campaignId });
  }

  // Conversations
  startConversation(body: {
    campaign_id?: Uuid | null;
    context?: ScreenContext | null;
    /** `research`: New research (needs a campaign); then `assistantOpening`. */
    purpose?: import("./types.ts").ConversationPurpose | null;
    /** The email design the conversation belongs to: its live conversation is returned when
     * there is one (on any device), else one starts in the design's campaign. */
    design_id?: Uuid | null;
    /** With design_id: a new conversation for the design even when one is live. */
    fresh?: boolean;
    /** With design_id: resume the live one only. With none live the answer is 204 and this
     * resolves to undefined; nothing is made. */
    resume_only?: boolean;
  }) {
    return this.client.post<AssistantSession>("/v1/assistant/sessions", {
      kind: "campaigns",
      ...body,
    });
  }
  conversations(campaignId?: Uuid | null, signal?: AbortSignal) {
    return this.client.get<{ items: ConversationSummary[] }>(
      `/v1/assistant/sessions${qs({ kind: "campaigns", campaign_id: campaignId })}`,
      signal,
    );
  }
  /** The rep's Assistant conversations: pinned first, then by last activity; paged and searched. */
  conversationPage(p: ConversationSearch = {}, signal?: AbortSignal) {
    return this.client.get<ConversationPage>(
      `/v1/assistant/conversations${qs({
        campaign_id: p.campaign_id,
        q: p.q?.trim() || undefined,
        cursor: p.cursor,
        limit: p.limit,
        archived: p.archived ? true : undefined,
      })}`,
      signal,
    );
  }
  /** Rename, pin or unpin, archive or unarchive (archiving unpins). */
  patchConversation(id: Uuid, body: { title?: string; pinned?: boolean; archived?: boolean }) {
    return this.client.patch<ConversationItem>(`/v1/assistant/conversations/${id}`, body);
  }
  /** Delete (undo with `restoreConversation`). Waiting cards in it expire. */
  deleteConversation(id: Uuid) {
    return this.client.del<ConversationDeleted>(`/v1/assistant/conversations/${id}`);
  }
  restoreConversation(id: Uuid) {
    return this.client.post<ConversationItem>(`/v1/assistant/conversations/${id}/restore`, {});
  }
  postContext(sessionId: Uuid, context: ScreenContext) {
    return this.client.post<void>(`/v1/assistant/sessions/${sessionId}/context`, context);
  }
  budget(signal?: AbortSignal) {
    return this.client.get<BudgetView>("/v1/assistant/budget", signal);
  }

  // Instructions
  instructions(campaignId?: Uuid | null, signal?: AbortSignal) {
    return this.client.get<Instruction>(
      `/v1/assistant/instructions${qs(campaignId ? { scope: "user_campaign", campaign_id: campaignId } : {})}`,
      signal,
    );
  }
  saveInstructions(body: { body_md: string; version: number }, campaignId?: Uuid | null) {
    return this.client.put<Instruction>(
      `/v1/assistant/instructions${qs(campaignId ? { scope: "user_campaign", campaign_id: campaignId } : {})}`,
      body,
    );
  }
  /** Admins: the company's template. */
  saveBriefTemplate(body_md: string) {
    return this.client.put<BriefTemplate>("/v1/admin/brief-template", { body_md });
  }
  /** Admins: back to the product default. */
  resetBriefTemplate() {
    return this.client.del<BriefTemplate>("/v1/admin/brief-template");
  }
  bundle(campaignId?: Uuid | null, signal?: AbortSignal) {
    return this.client.get<InstructionBundle>(
      `/v1/assistant/instructions/bundle${qs({ campaign_id: campaignId })}`,
      signal,
    );
  }

  // Suggestions
  suggestions(
    p: { status?: string; campaign_id?: Uuid | null; kind?: SuggestionKind; page?: number } = {},
    signal?: AbortSignal,
  ) {
    return this.client.get<SuggestionList>(`/v1/suggestions${qs(p)}`, signal);
  }
  suggestion(id: Uuid, signal?: AbortSignal) {
    return this.client.get<Suggestion>(`/v1/suggestions/${id}`, signal);
  }
  editSuggestion(id: Uuid, payload: unknown) {
    return this.client.patch<Suggestion>(`/v1/suggestions/${id}`, { payload });
  }
  /**
   * The Create list dialog on a call list card: the list's name (in the payload) and where it
   * goes. `campaign_id: null` puts it in no campaign. Nothing is made until accept.
   */
  placeSuggestion(id: Uuid, body: { payload?: unknown; campaign_id: Uuid | null }) {
    return this.client.patch<Suggestion>(`/v1/suggestions/${id}`, {
      ...(body.payload === undefined ? {} : { payload: body.payload }),
      ...(body.campaign_id ? { campaign_id: body.campaign_id } : { no_campaign: true }),
    });
  }
  /** Throws PausedError when a subject was paused since the card was made. */
  async accept(
    id: Uuid,
    body: { payload?: unknown; include_paused?: boolean; only?: string[] } = {},
  ) {
    try {
      return await this.client.post<Suggestion>(`/v1/suggestions/${id}/accept`, body);
    } catch (e) {
      const err = e as { status?: number; body?: { paused?: PausedSubjects } };
      if (err.status === 423) {
        throw new PausedError(err.body?.paused ?? { contacts: [], lists: [], campaigns: [] });
      }
      throw e;
    }
  }
  dismiss(id: Uuid, body: DismissSuggestion = {}) {
    return this.client.post<Suggestion>(`/v1/suggestions/${id}/dismiss`, body);
  }
  /**
   * Takes an applied brief change back (409 `brief_changed` when its text changed since), or a
   * tracker or list change applied at the person's request in chat.
   */
  undo(id: Uuid) {
    return this.client.post<Suggestion>(`/v1/suggestions/${id}/undo`);
  }

  // Pauses
  pauses(signal?: AbortSignal) {
    return this.client.get<{ items: AiPause[] }>("/v1/ai/pauses", signal);
  }
  pause(body: NewPause) {
    return this.client.post<AiPause>("/v1/ai/pauses", body);
  }
  resume(id: Uuid) {
    return this.client.del<void>(`/v1/ai/pauses/${id}`);
  }
  aiState(subject: AiSubject, signal?: AbortSignal) {
    const s = subject as { contact?: CrmRef; list_id?: Uuid; campaign_id?: Uuid };
    return this.client.get<AiState>(
      `/v1/ai/state${qs({
        contact: s.contact ? `${s.contact.model}:${s.contact.id}` : null,
        list_id: s.list_id,
        campaign_id: s.campaign_id,
      })}`,
      signal,
    );
  }
}
